import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VersionUpdaterService } from '../version-updater.service';

export interface EditVersionDialogData {
  version?: any; // If editing existing version
  isEdit: boolean; // true for edit, false for add new
}

@Component({
  selector: 'app-edit-version-updater',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './edit-version-updater.component.html',
  styleUrl: './edit-version-updater.component.scss'
})
export class EditVersionUpdaterComponent implements OnInit {
  
  versionForm!: FormGroup;
  isEdit: boolean;
  isLoading: boolean = false;
  lookupMessage: string = '';
  lookupSuccess: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<EditVersionUpdaterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditVersionDialogData,
    private formBuilder: FormBuilder,
    private versionUpdaterService: VersionUpdaterService
  ) {
    this.isEdit = data?.isEdit || false;
    this.initializeForm();
  }

  ngOnInit(): void {
    if (this.isEdit && this.data?.version) {
      this.populateForm(this.data.version);
    }
  }

  private initializeForm(): void {
    // Get current date and time in the format required for datetime-local input
    const now = new Date();
    const currentDateTime = now.toISOString().slice(0, 16); // Format: 2025-11-24T11:50
    
    // Set default date for new versions, empty for edit mode
    const defaultDate = this.isEdit ? '' : currentDateTime;
    
    this.versionForm = this.formBuilder.group({
      date: [{value: defaultDate, disabled: true}, Validators.required],
      folder: ['', Validators.required],
      version: [{value: '', disabled: true}, [Validators.required, Validators.pattern(/^\d+\.\d+\.\d+$/)]],
      critical: [false],
      description: [{value: '', disabled: true}],
      published: [false]
    });
  }

  private populateForm(version: any): void {
    // Handle both property naming conventions: is_critical/critical and is_published/isPublished
    const isCritical = version.is_critical === '1' || version.is_critical === 1 || version.is_critical === true 
                    || version.critical === '1' || version.critical === 1 || version.critical === true;
    const isPublished = version.is_published === '1' || version.is_published === 1 || version.is_published === true
                     || version.isPublished === '1' || version.isPublished === 1 || version.isPublished === true;
    
    console.log('Populating form with version data:', {
      version: version,
      isCritical: isCritical,
      isPublished: isPublished
    });
    
    this.versionForm.patchValue({
      date: version.date || '',
      folder: version.folder || '',
      version: `${version.major}.${version.minor}.${version.patch}`,
      critical: isCritical,
      description: version.description || '',
      published: isPublished
    });
    
    // Alternative: Set values individually if patchValue doesn't work
    this.versionForm.get('critical')?.setValue(isCritical);
    this.versionForm.get('published')?.setValue(isPublished);
    
    console.log('Form values after patch:', this.versionForm.value);
  }

  onLookup(): void {
    // Call API to get folder details when lookup button is clicked
    this.callFolderDetailsAPI();
  }

  private callFolderDetailsAPI(): void {
    this.isLoading = true;
    this.lookupMessage = ''; // Clear previous message
    
    // Get folder path from the form input
    const folderPath = this.versionForm.get('folder')?.value || 'default-path';
    
    const payload = {
      folderPath: folderPath
    };

    console.log('Calling folder details API with payload:', payload);

    this.versionUpdaterService.getFolderDetails(payload).subscribe({
      next: (response) => {
        console.log('Folder details API response:', response);
        this.isLoading = false;
        
        // Check if response has the expected structure
        if (response?.status === 'SUCCESS' && response?.data?.data) {
          const responseData = response.data.data;
          
          // Show success message
          this.lookupMessage = 'Notice! This version is already saved.';
          this.lookupSuccess = true;
          
          // Bind the response data to form fields
          this.versionForm.patchValue({
            version: responseData.update_ver || '', // Bind update_ver to version field
            critical: responseData['update-type'] === 1 || responseData['update-type'] === '1', // Bind update-type to critical (1 = true, 0 = false)
            description: responseData.description || '', // Bind description to description field
            published: responseData.update_available === 'true' || responseData.update_available === true || responseData.update_available === 1 // Bind update_available to published checkbox
          });
          
          console.log('Form updated with API data:', {
            version: responseData.update_ver,
            critical: responseData['update-type'],
            description: responseData.description,
            published: responseData.update_available
          });
        } else {
          // Show error message - folder not found
          this.lookupMessage = 'Folder not found!';
          this.lookupSuccess = false;
          console.warn('API response does not have expected structure:', response);
        }
      },
      error: (error) => {
        console.error('Error calling folder details API:', error);
        this.isLoading = false;
        
        // Show error message
        this.lookupMessage = 'Folder not found!';
        this.lookupSuccess = false;
      }
    });
  }

  onSave(): void {
    if (this.versionForm.valid) {
      const formValue = this.versionForm.getRawValue(); // Use getRawValue to include disabled fields
      
      // Convert datetime-local format to the required format (YYYY-MM-DD HH:mm:ss)
      let formattedDate = formValue.date;
      if (formValue.date) {
        // datetime-local format is "2025-11-25T10:30", convert to "2025-11-25 10:30:00"
        formattedDate = formValue.date.replace('T', ' ') + ':00';
      }
      
      const versionData = {
        date: formattedDate,
        folder: formValue.folder,
        version: formValue.version,
        isCritical: formValue.critical ? 1 : 0,
        description: formValue.description || '',
        isPublished: formValue.published ? 1 : 0
      };

      console.log('Save payload:', versionData);

      // Show loading state
      this.isLoading = true;

      // Call API to save/update version
      this.versionUpdaterService.saveUpdateVersion(versionData).subscribe({
        next: (response) => {
          console.log('Save version API response:', response);
          this.isLoading = false;
          
          // Close dialog with success result
          this.dialogRef.close({
            saved: true,
            data: versionData,
            response: response,
            isEdit: this.isEdit
          });
        },
        error: (error) => {
          console.error('Error saving version:', error);
          this.isLoading = false;
          // You can show error message to user here
          // For now, we'll still close the dialog but indicate failure
          this.dialogRef.close({
            saved: false,
            error: error,
            isEdit: this.isEdit
          });
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.versionForm.markAllAsTouched();
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  getDialogTitle(): string {
    return this.isEdit ? 'Edit Version Details' : 'Add New Version';
  }
}
